import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit
} from '@angular/core';

import { Router } from '@angular/router';

import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { HttpErrorResponse } from '@angular/common/http';

import { AuthService } from '../../api-authorization/authorize.service';

import {
  UserModel,
  User
} from '../../user.model';

import { UserFormComponent } from '../user-form/user-form.component';

import { IdentityResult } from '../../api-authorization/identity-result.model';

import {
  ResetPasswordDialogComponent
} from '../user-form/reset-password-dialog/reset-password-dialog.component';

import {
  ConfirmDeleteDialogComponent
} from './confirm-delete-dialog/confirm-delete-dialog.component';

import {
  QrCodeAdminComponent
} from '../Admin/qr-code-admin/qr-code-admin.component';


@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.css']
})
export class UserListComponent
  implements OnInit, AfterViewInit {

  users: UserModel[] = [];

  displayedColumns: string[] = [
    'userName',
    'email',
    'rolle',
    'actions'
  ];

  dataSource =
    new MatTableDataSource<UserModel>(
      this.users
    );

  isLoading = false;

  filterValue = '';

  totalUsers = 0;

  totalAdmins = 0;

  totalStandardUsers = 0;

  resetInProgress:
    Record<string, boolean> = {};

  deleteInProgress:
    Record<string, boolean> = {};


  @ViewChild(MatSort)
  sort!: MatSort;


  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) { }


  // ========================================================
  // INIT
  // ========================================================

  ngOnInit(): void {

    this.dataSource.filterPredicate =
      (
        data: UserModel,
        filter: string
      ) => {

        const searchable =
          `${data.username} ${data.email} ${
            typeof data.roles === 'string'
              ? data.roles
              : (data.roles || []).join(', ')
          }`
            .toLowerCase();

        return searchable.includes(
          filter
        );
      };


    this.loadUsers();
  }


  ngAfterViewInit(): void {

    this.dataSource.sort =
      this.sort;
  }


  // ========================================================
  // KATEGORIEN VERWALTEN
  // ========================================================

  openKategorieAdmin(): void {

    this.router.navigate([
      '/admin/kategorien'
    ]);

  }


  openQrCodeAdmin(): void {

    this.dialog.open(
      QrCodeAdminComponent,
      {
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false
      }
    );

  }


  // ========================================================
  // FEHLER
  // ========================================================

  private handleError(
    error:
      HttpErrorResponse
      | IdentityResult,
    action: string = ''
  ): void {

    console.error(
      error
    );


    let errorMessage =
      'Ein unbekannter Fehler ist aufgetreten.';


    if (
      error instanceof HttpErrorResponse
    ) {

      if (
        error.error
        &&
        error.error.message
      ) {

        errorMessage =
          error.error.message;

      } else if (
        typeof error.error === 'string'
      ) {

        errorMessage =
          error.error;

      } else {

        errorMessage =
          `Fehler ${error.status}: ${error.statusText}`;

      }

    } else {

      errorMessage =
        error.errors
          .map(
            e =>
              `${e.code}: ${e.description}`
          )
          .join('\n');

    }


    this.snackBar.open(
      errorMessage,
      'Schließen',
      {
        duration: 5000,
        panelClass: [
          'error-snackbar'
        ]
      }
    );


    this.isLoading =
      false;
  }


  // ========================================================
  // BENUTZER LADEN
  // ========================================================

  loadUsers(): void {

    this.isLoading =
      true;


    this.authService
      .getUsers()
      .subscribe({

        next: users => {

          this.totalUsers =
            users.length;


          this.totalAdmins =
            users.filter(
              user =>
                this.hasRole(
                  user,
                  'Admin'
                )
            ).length;


          this.totalStandardUsers =
            users.filter(
              user =>
                this.hasRole(
                  user,
                  'user'
                )
            ).length;


          this.users =
            users.map(
              user => ({

                ...user,

                roles:
                  this.formatRoles(
                    user.roles
                  ),

                role:
                  this.toRoleArray(
                    user.roles
                  )[0]
                  ||
                  (
                    typeof user.roles
                    === 'string'
                      ? user.roles
                      : ''
                  )

              })
            );


          this.dataSource.data =
            this.users;


          if (this.sort) {

            this.dataSource.sort =
              this.sort;

          }


          if (this.filterValue) {

            this.applyFilter(
              this.filterValue
            );

          }


          this.isLoading =
            false;
        },


        error: error => {

          this.handleError(
            error,
            'Laden der Benutzer'
          );

          this.isLoading =
            false;

        }

      });
  }


  // ========================================================
  // FILTER
  // ========================================================

  applyFilter(
    filterValue: string
  ): void {

    this.filterValue =
      filterValue;


    this.dataSource.filter =
      filterValue
        .trim()
        .toLowerCase();
  }


  clearFilter(): void {

    this.filterValue = '';

    this.dataSource.filter = '';

  }


  // ========================================================
  // BENUTZER LÖSCHEN
  // ========================================================

  deleteUser(
    userId: string
  ): void {

    const user =
      this.users.find(
        u => u.id === userId
      );


    const username =
      user?.username
      ||
      'diesen Benutzer';


    const dialogRef =
      this.dialog.open(
        ConfirmDeleteDialogComponent,
        {

          width: '400px',

          data: {

            title:
              'Benutzer löschen',

            message:
              `Möchten Sie den Benutzer "${username}" wirklich löschen?`,

            confirmText:
              'Löschen',

            cancelText:
              'Abbrechen'

          }

        }
      );


    dialogRef
      .afterClosed()
      .subscribe(
        confirmed => {

          if (confirmed) {

            this.performDelete(
              userId,
              username
            );

          }

        }
      );
  }


  private performDelete(
    userId: string,
    username: string
  ): void {

    this.deleteInProgress[
      userId
    ] = true;


    this.authService
      .deleteUser(
        userId
      )
      .subscribe({

        next: (
          response: any
        ) => {

          this.deleteInProgress[
            userId
          ] = false;


          const message =
            response?.message
            ||
            `Benutzer "${username}" wurde erfolgreich gelöscht.`;


          this.snackBar.open(
            message,
            'OK',
            {
              duration: 4000,
              panelClass: [
                'success-snackbar'
              ]
            }
          );


          this.loadUsers();

        },


        error: (
          error:
            HttpErrorResponse
        ) => {

          this.deleteInProgress[
            userId
          ] = false;


          this.handleError(
            error,
            'Löschen des Benutzers'
          );

        }

      });
  }


  // ========================================================
  // BENUTZER ANLEGEN
  // ========================================================

  openCreateUserDialog(): void {

    const dialogRef =
      this.dialog.open(
        UserFormComponent,
        {

          width: '400px',

          data: {
            user: new User(),
            isEditMode: false
          }

        }
      );


    dialogRef
      .afterClosed()
      .subscribe(
        (
          result:
            UserModel
            | null
        ) => {

          if (result) {

            this.loadUsers();

          }

        }
      );
  }


  // ========================================================
  // BENUTZER BEARBEITEN
  // ========================================================

  openEditUserDialog(
    user: UserModel
  ): void {

    const dialogRef =
      this.dialog.open(
        UserFormComponent,
        {

          width: '400px',

          data: {

            user: {
              ...user
            },

            isEditMode:
              true

          }

        }
      );


    dialogRef
      .afterClosed()
      .subscribe(
        (
          result:
            UserModel
            | null
        ) => {

          if (result) {

            this.loadUsers();

          }

        }
      );
  }


  // ========================================================
  // PASSWORT ZURÜCKSETZEN
  // ========================================================

  openResetPasswordDialog(
    user: UserModel
  ): void {

    const dialogRef =
      this.dialog.open(
        ResetPasswordDialogComponent,
        {

          width: '420px',

          data: {
            user
          }

        }
      );


    dialogRef
      .afterClosed()
      .subscribe(
        confirmed => {

          if (confirmed) {

            this.resetPassword(
              user
            );

          }

        }
      );
  }


  resetPassword(
    user: UserModel
  ): void {

    const userId =
      user.id
      ||
      '';


    if (!userId) {

      this.snackBar.open(
        'Benutzer-ID nicht gefunden.',
        'Schließen',
        {
          duration: 4000
        }
      );

      return;
    }


    this.resetInProgress[
      userId
    ] = true;


    this.authService
      .resetPassword(
        userId
      )
      .subscribe({

        next: response => {

          this.resetInProgress[
            userId
          ] = false;


          const message =
            response?.message
            ||
            'Ein Einmalpasswort wurde versendet.';


          this.snackBar.open(
            message,
            'OK',
            {
              duration: 4000,
              panelClass: [
                'success-snackbar'
              ]
            }
          );

        },


        error: (
          error:
            HttpErrorResponse
        ) => {

          this.resetInProgress[
            userId
          ] = false;


          this.handleError(
            error,
            'Zurücksetzen des Passworts'
          );

        }

      });
  }


  // ========================================================
  // ROLLEN
  // ========================================================

  private toRoleArray(
    roles:
      string
      | string[]
      | undefined
  ): string[] {

    if (
      Array.isArray(
        roles
      )
    ) {

      return roles;

    }


    if (
      typeof roles
      === 'string'
    ) {

      return roles
        .split(',')
        .map(
          role =>
            role.trim()
        )
        .filter(
          role =>
            role.length > 0
        );

    }


    return [];
  }


  private formatRoles(
    roles:
      string
      | string[]
      | undefined
  ): string {

    const values =
      this.toRoleArray(
        roles
      );


    if (
      values.length === 0
      &&
      typeof roles === 'string'
    ) {

      return roles;

    }


    return values.join(
      ', '
    );
  }


  private hasRole(
    user: UserModel,
    role: string
  ): boolean {

    return this
      .toRoleArray(
        user.roles
      )
      .some(
        r =>
          r.toLowerCase()
          ===
          role.toLowerCase()
      );
  }
}